import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAdminPermissions } from '../contexts/AdminPermissionsContext';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { firestore } from '../firebase';
import './FreshMeetHqDashboard.css';
import { getActiveEventId } from '../utils/activeEvent';
import { fetchSponsorsMergedForEvent } from '../utils/sponsorQueries';
import MainClueSponsorRunAdmin from './MainClueSponsorRunAdmin';
import {
  ensureMainClueEventConfigMigrated,
  subscribeMainClueEventConfig,
  saveSetAssignments,
} from '../utils/mainClueEventConfig';

const SET_COLORS = {
  A: '#00e5b0', // teal
  B: '#a87fff', // purple
  C: '#ff6b6b', // coral
};

const GOLD_FINAL = '#f5a623';
const BG_NAVY = '#0a1628';

const DIVE_COLORS = {
  'Dive 1': '#0ea5e9',
  'Dive 2': '#00e5b0',
  'Dive 3': '#a87fff',
  'Dive 4': GOLD_FINAL,
};

const TEAM_GROUPS = [
  { id: 'A', label: 'Teams 1–16', range: '1–16' },
  { id: 'B', label: 'Teams 17–33', range: '17–33' },
  { id: 'C', label: 'Teams 34–50', range: '34–50' },
];

const SET_META = {
  A: {
    id: 'A',
    label: 'Set A',
    color: SET_COLORS.A,
  },
  B: {
    id: 'B',
    label: 'Set B',
    color: SET_COLORS.B,
  },
  C: {
    id: 'C',
    label: 'Set C',
    color: SET_COLORS.C,
  },
};

const CASHED_COLUMNS = [
  { id: 'A', label: 'Cashed Set A chips', setId: 'A' },
  { id: 'B', label: 'Cashed Set B chips', setId: 'B' },
  { id: 'C', label: 'Cashed Set C chips', setId: 'C' },
];

// Mapping from (group, cashed set) -> envelope
const GRID_CELLS = [
  // Group A: Teams 1–16
  {
    id: 'A-A',
    groupId: 'A',
    cashedSetId: 'A',
    label: 'Dive 2 + Set B',
    diveLabel: 'Dive 2',
    nextSetId: 'B',
    hasEnvelope: true,
  },
  {
    id: 'A-B',
    groupId: 'A',
    cashedSetId: 'B',
    label: 'Dive 3 + Set C',
    diveLabel: 'Dive 3',
    nextSetId: 'C',
    hasEnvelope: true,
  },
  {
    id: 'A-C',
    groupId: 'A',
    cashedSetId: 'C',
    label: 'Dive 4 only',
    diveLabel: 'Dive 4',
    nextSetId: null,
    hasEnvelope: true,
  },
  // Group B: Teams 17–33
  {
    id: 'B-A',
    groupId: 'B',
    cashedSetId: 'A',
    label: 'Dive 4 only',
    diveLabel: 'Dive 4',
    nextSetId: null,
    hasEnvelope: true,
  },
  {
    id: 'B-B',
    groupId: 'B',
    cashedSetId: 'B',
    label: 'Dive 2 + Set C',
    diveLabel: 'Dive 2',
    nextSetId: 'C',
    hasEnvelope: true,
  },
  {
    id: 'B-C',
    groupId: 'B',
    cashedSetId: 'C',
    label: 'Dive 3 + Set A',
    diveLabel: 'Dive 3',
    nextSetId: 'A',
    hasEnvelope: true,
  },
  // Group C: Teams 34–50
  {
    id: 'C-A',
    groupId: 'C',
    cashedSetId: 'A',
    label: 'Dive 3 + Set B',
    diveLabel: 'Dive 3',
    nextSetId: 'B',
    hasEnvelope: true,
  },
  {
    id: 'C-B',
    groupId: 'C',
    cashedSetId: 'B',
    label: 'Dive 4 only',
    diveLabel: 'Dive 4',
    nextSetId: null,
    hasEnvelope: true,
  },
  {
    id: 'C-C',
    groupId: 'C',
    cashedSetId: 'C',
    label: 'Dive 2 + Set A',
    diveLabel: 'Dive 2',
    nextSetId: 'A',
    hasEnvelope: true,
  },
];

const CHECKLIST_STORAGE_KEY = 'freshmeet2026-hq-checklist-v1';
const MAIN_CLUE_TAB = {
  dashboard: 'dashboard',
  sponsorRun: 'sponsor-run',
};

const EXTRA_CHECKLIST_IDS = [
  'FIRST_ENVELOPE', // Main Clue 1 – everyone
  'DIVE1_A',        // Dive 1 + Set A (Teams 1–16)
  'DIVE1_B',        // Dive 1 + Set B (Teams 17–33)
  'DIVE1_C',        // Dive 1 + Set C (Teams 34–50)
];

function safeLoadChecklist() {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(CHECKLIST_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

function safeSaveChecklist(state) {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(CHECKLIST_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

const SponsorChip = ({ sponsor, color }) => {
  const [useFallback, setUseFallback] = useState(!sponsor.logo);
  const handleError = () => setUseFallback(true);

  return (
    <div
      className="freshmeet-hq-chip"
      style={{ borderColor: color, color }}
      title={sponsor.label}
    >
      {!useFallback && sponsor.logo ? (
        <img
          src={sponsor.logo}
          alt={sponsor.label}
          onError={handleError}
          className="freshmeet-hq-chip-logo"
        />
      ) : (
        <span className="freshmeet-hq-chip-abbr">{sponsor.abbr}</span>
      )}
    </div>
  );
};

const SponsorChipRow = ({ sponsors, color, labelPrefix }) => {
  if (!sponsors || sponsors.length === 0) return null;
  return (
    <div className="freshmeet-hq-chip-row" aria-label={`${labelPrefix} sponsors`}>
      {sponsors.map((s) => (
        <SponsorChip key={s.id} sponsor={s} color={color} />
      ))}
    </div>
  );
};

function getCell(groupId, cashedSetId) {
  return GRID_CELLS.find((c) => c.groupId === groupId && c.cashedSetId === cashedSetId);
}

const FreshMeetHqDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = useAdminPermissions();
  const showHqDashboard = hasPermission('events') || hasPermission('freshmeetHq');
  const showSponsorRunTools = hasPermission('sponsors') || showHqDashboard;
  const showMainClueTabBar = showHqDashboard;

  const tabParam = (searchParams.get('tab') || '').trim().toLowerCase();
  const urlMainClueTab = tabParam === MAIN_CLUE_TAB.sponsorRun
    ? MAIN_CLUE_TAB.sponsorRun
    : MAIN_CLUE_TAB.dashboard;

  const activeMainClueTab = useMemo(() => {
    if (!showHqDashboard && showSponsorRunTools) return MAIN_CLUE_TAB.sponsorRun;
    return urlMainClueTab;
  }, [showHqDashboard, showSponsorRunTools, urlMainClueTab]);

  useEffect(() => {
    if (!showHqDashboard && showSponsorRunTools && urlMainClueTab === MAIN_CLUE_TAB.dashboard) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', MAIN_CLUE_TAB.sponsorRun);
          return next;
        },
        { replace: true }
      );
    }
  }, [showHqDashboard, showSponsorRunTools, urlMainClueTab, setSearchParams]);
  const [mode, setMode] = useState('stuffing'); // 'stuffing' | 'lookup'
  const [activeEvent, setActiveEvent] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  const [loadingSponsors, setLoadingSponsors] = useState(true);
  const [firestoreSetMap, setFirestoreSetMap] = useState({});
  const firestoreSetMapRef = useRef({});
  useEffect(() => {
    firestoreSetMapRef.current = firestoreSetMap;
  }, [firestoreSetMap]);

  const assignments = useMemo(() => {
    const merged = {};
    sponsors.forEach((s) => {
      const v = firestoreSetMap[s.id];
      merged[s.id] = v === 'A' || v === 'B' || v === 'C' ? v : null;
    });
    return merged;
  }, [sponsors, firestoreSetMap]);

  const [showSettings, setShowSettings] = useState(false);
  const envelopeCells = useMemo(
    () => GRID_CELLS.filter((c) => c.hasEnvelope),
    []
  );

  const allChecklistIds = useMemo(
    () => [...EXTRA_CHECKLIST_IDS, ...envelopeCells.map((c) => c.id)],
    [envelopeCells]
  );

  const [placedMap, setPlacedMap] = useState(() => {
    const saved = safeLoadChecklist();
    const allIds = [...EXTRA_CHECKLIST_IDS, ...GRID_CELLS.filter((c) => c.hasEnvelope).map((c) => c.id)];
    const initial = {};
    allIds.forEach((id) => {
      initial[id] = !!(saved && saved[id]);
    });
    return initial;
  });

  useEffect(() => {
    let cancelled = false;
    async function loadEventAndSponsors() {
      try {
        setLoadingSponsors(true);
        let eventDoc = null;
        const id = await getActiveEventId();
        if (id) {
          const eventSnap = await getDocs(query(collection(firestore, 'events'), where('__name__', '==', id)));
          if (!eventSnap.empty) {
            const d = eventSnap.docs[0];
            eventDoc = { id: d.id, ...d.data() };
          }
        }
        if (cancelled) return;
        setActiveEvent(eventDoc);

        if (!eventDoc) {
          setSponsors([]);
          setFirestoreSetMap({});
          return;
        }

        const merged = await fetchSponsorsMergedForEvent(eventDoc.id);
        if (cancelled) return;
        const list = merged
          .filter(
            (s) =>
              s &&
              (s.type || 'none') !== 'none' &&
              s.showOnDashboard !== false
          )
          .sort((a, b) => {
            const ta = (a.type || 'none').toString();
            const tb = (b.type || 'none').toString();
            const ia = ta === 'interactive' ? 0 : 1;
            const ib = tb === 'interactive' ? 0 : 1;
            if (ia !== ib) return ia - ib;
            return (a.name || '').toString().toLowerCase().localeCompare((b.name || '').toString().toLowerCase());
          });
        setSponsors(list);
      } catch (err) {
        console.error('Failed to load sponsors for Fresh Meet HQ:', err);
        setSponsors([]);
        setFirestoreSetMap({});
      } finally {
        if (!cancelled) setLoadingSponsors(false);
      }
    }
    loadEventAndSponsors();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!activeEvent?.id) {
      setFirestoreSetMap({});
      return;
    }
    let cancelled = false;
    let unsub = () => {};
    (async () => {
      try {
        await ensureMainClueEventConfigMigrated(activeEvent.id);
        if (cancelled) return;
        unsub = subscribeMainClueEventConfig(activeEvent.id, (data) => {
          setFirestoreSetMap(data.setAssignments || {});
        });
      } catch (err) {
        console.error('Main clue event config subscription failed:', err);
      }
    })();
    return () => {
      cancelled = true;
      unsub();
    };
  }, [activeEvent?.id]);

  const sponsorsBySet = useMemo(() => {
    const map = {
      A: [],
      B: [],
      C: [],
    };
    const makeAbbr = (s) => {
      const fromField = (s.abbreviation || s.abbr || '').toString().trim();
      if (fromField) return fromField.slice(0, 3).toUpperCase();
      const name = (s.name || '').toString().trim();
      if (!name) return (s.id || '').toString().slice(0, 3).toUpperCase();
      const first = name.split(/\s+/)[0] || name;
      return first.slice(0, 3).toUpperCase();
    };
    sponsors.forEach((s) => {
      const setId = assignments[s.id];
      if (setId === 'A' || setId === 'B' || setId === 'C') {
        map[setId].push({
          id: s.id,
          label: s.name || s.companyName || s.id,
          abbr: makeAbbr(s),
          logo: (s.logo || '').trim() || null,
        });
      }
    });
    return map;
  }, [sponsors, assignments]);

  useEffect(() => {
    safeSaveChecklist(placedMap);
  }, [placedMap]);

  const placedCount = useMemo(
    () =>
      allChecklistIds.reduce((acc, id) => (placedMap[id] ? acc + 1 : acc), 0),
    [allChecklistIds, placedMap]
  );
  const totalEnvelopes = allChecklistIds.length;
  const progressPct = totalEnvelopes > 0 ? Math.round((placedCount / totalEnvelopes) * 100) : 0;

  const handleToggleCell = (cell) => {
    if (!cell.hasEnvelope || mode !== 'stuffing') return;
    setPlacedMap((prev) => ({
      ...prev,
      [cell.id]: !prev[cell.id],
    }));
  };

  const handleToggleExtra = (id) => {
    if (mode !== 'stuffing') return;
    setPlacedMap((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleChangeAssignment = (sponsorId, nextSetId) => {
    if (!activeEvent?.id) return;
    const val = nextSetId === 'none' ? null : nextSetId;
    const cleaned = { ...firestoreSetMapRef.current };
    if (val === 'A' || val === 'B' || val === 'C') cleaned[sponsorId] = val;
    else delete cleaned[sponsorId];
    saveSetAssignments(activeEvent.id, cleaned).catch((e) => console.error(e));
  };

  const handleReset = () => {
    // Simple confirm to avoid accidental wipes
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line no-alert
      const ok = window.confirm('Reset stuffing checklist for all envelopes?');
      if (!ok) return;
    }
    setPlacedMap(() => {
      const next = {};
      allChecklistIds.forEach((id) => {
        next[id] = false;
      });
      return next;
    });
  };

  const setMainClueTab = (tabId) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tabId === MAIN_CLUE_TAB.sponsorRun) next.set('tab', MAIN_CLUE_TAB.sponsorRun);
      else next.delete('tab');
      return next;
    });
  };

  if (!showHqDashboard && !showSponsorRunTools) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div className="admin-dashboard freshmeet-hq-root">
      <div className="freshmeet-hq-shell">
        {showMainClueTabBar ? (
        <div className="freshmeet-hq-main-tabs" role="tablist" aria-label="Main clue workspace">
          <button
            type="button"
            role="tab"
            aria-selected={activeMainClueTab === MAIN_CLUE_TAB.dashboard}
            className={`freshmeet-hq-main-tab ${
              activeMainClueTab === MAIN_CLUE_TAB.dashboard ? 'freshmeet-hq-main-tab--active' : ''
            }`}
            onClick={() => setMainClueTab(MAIN_CLUE_TAB.dashboard)}
          >
            Main clue dashboard
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeMainClueTab === MAIN_CLUE_TAB.sponsorRun}
            className={`freshmeet-hq-main-tab ${
              activeMainClueTab === MAIN_CLUE_TAB.sponsorRun ? 'freshmeet-hq-main-tab--active' : ''
            }`}
            onClick={() => setMainClueTab(MAIN_CLUE_TAB.sponsorRun)}
          >
            Sponsor Run questions
          </button>
        </div>
        ) : null}
        {activeMainClueTab === MAIN_CLUE_TAB.sponsorRun ? (
          <MainClueSponsorRunAdmin />
        ) : (
          <>
        <header className="freshmeet-hq-header">
          <div className="freshmeet-hq-title-block">
            <h1 className="freshmeet-hq-title">Fresh Meet Main Clue</h1>
            <p className="freshmeet-hq-subtitle">
              Pre-event stuffing checklist and live lookup table for envelope handoff at HQ.
            </p>
          </div>
          <div className="freshmeet-hq-mode-toggle" role="radiogroup" aria-label="Dashboard mode">
            <button
              type="button"
              className={
                mode === 'stuffing'
                  ? 'freshmeet-hq-mode-btn freshmeet-hq-mode-btn--active'
                  : 'freshmeet-hq-mode-btn'
              }
              onClick={() => setMode('stuffing')}
              role="radio"
              aria-checked={mode === 'stuffing'}
            >
              Stuffing checklist
            </button>
            <button
              type="button"
              className={
                mode === 'lookup'
                  ? 'freshmeet-hq-mode-btn freshmeet-hq-mode-btn--active'
                  : 'freshmeet-hq-mode-btn'
              }
              onClick={() => setMode('lookup')}
              role="radio"
              aria-checked={mode === 'lookup'}
            >
              Live lookup
            </button>
          </div>
        </header>

        <section className="freshmeet-hq-settings">
          <button
            type="button"
            className="freshmeet-hq-settings-header"
            onClick={() => setShowSettings((v) => !v)}
            aria-expanded={showSettings}
          >
            <div className="freshmeet-hq-settings-title-wrap">
              <span className="freshmeet-hq-settings-chevron">
                {showSettings ? '▾' : '▸'}
              </span>
              <h2 className="freshmeet-hq-settings-title">Question set sponsors</h2>
            </div>
            <span className="freshmeet-hq-settings-header-hint">
              {showSettings ? 'Hide' : 'Show'} sponsor-to-set mapping
            </span>
          </button>
          {showSettings && (
            <>
              <p className="freshmeet-hq-settings-help">
                Using active sponsors for{' '}
                {activeEvent ? (activeEvent.title || 'this event') : 'the active event'}
                . Choose which question set each sponsor belongs to.
              </p>
              {loadingSponsors ? (
                <p className="freshmeet-hq-settings-status">Loading sponsors…</p>
              ) : sponsors.length === 0 ? (
                <p className="freshmeet-hq-settings-status">
                  No active dashboard sponsors found for the current event.
                </p>
              ) : (
                <div className="freshmeet-hq-settings-table-wrap">
                  <table className="freshmeet-hq-settings-table">
                    <thead>
                      <tr>
                        <th>Sponsor</th>
                        <th>Set</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sponsors.map((s) => {
                        const current = assignments[s.id] || 'none';
                        const logoUrl = (s.logo || '').trim();
                        return (
                          <tr key={s.id}>
                            <td className="freshmeet-hq-settings-sponsor-cell">
                              {logoUrl ? (
                                <img
                                  src={logoUrl}
                                  alt={s.name || ''}
                                  className="freshmeet-hq-settings-logo"
                                />
                              ) : (
                                <div className="freshmeet-hq-settings-logo-placeholder" />
                              )}
                              <span className="freshmeet-hq-settings-sponsor-name">
                                {s.name || 'Unnamed sponsor'}
                              </span>
                            </td>
                            <td>
                              <select
                                value={current}
                                onChange={(e) => handleChangeAssignment(s.id, e.target.value)}
                                className="freshmeet-hq-settings-select"
                              >
                                <option value="none">Not in question sets</option>
                                <option value="A">Set A</option>
                                <option value="B">Set B</option>
                                <option value="C">Set C</option>
                              </select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </section>

        <section className="freshmeet-hq-info">
          {mode === 'stuffing' ? (
            <p>
              Tap each envelope cell after you place that envelope in the physical grid.
            </p>
          ) : (
            <p>
              Use the table to match a team group row with the cashed chip set column. Cells are read-only in
              this mode so you can use the grid as a clean reference at the HQ table.
            </p>
          )}
        </section>

        <section className="freshmeet-hq-progress">
          <div className="freshmeet-hq-progress-main">
            <span className="freshmeet-hq-progress-label">
              {placedCount} of {totalEnvelopes} envelopes placed
            </span>
            <div className="freshmeet-hq-progress-bar">
              <div
                className="freshmeet-hq-progress-bar-fill"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
          <button
            type="button"
            className="freshmeet-hq-reset-btn"
            onClick={handleReset}
          >
            Reset checklist
          </button>
        </section>
        <section className="freshmeet-hq-grid-section">
          <div className="freshmeet-hq-grid-scroller">
            <table className="freshmeet-hq-grid" aria-label="FreshMeet envelope lookup grid">
              <thead>
                <tr>
                  <th className="freshmeet-hq-corner-cell">
                    <span className="freshmeet-hq-axis-label">Team group</span>
                    <span className="freshmeet-hq-axis-label-secondary">Rows</span>
                  </th>
                  {mode === 'stuffing' && (
                    <th className="freshmeet-hq-col-header freshmeet-hq-col-header--dive1">
                      <div className="freshmeet-hq-col-header-inner">
                        <div className="freshmeet-hq-col-label">Dive 1 + starting set</div>
                      </div>
                    </th>
                  )}
                  {CASHED_COLUMNS.map((col) => {
                    const set = SET_META[col.setId];
                    const chips = sponsorsBySet[col.setId];
                    return (
                      <th key={col.id} className="freshmeet-hq-col-header">
                        <div className="freshmeet-hq-col-header-inner">
                          <div
                            className="freshmeet-hq-col-label"
                            style={{
                              borderBottomColor: set.color,
                            }}
                          >
                            {col.label}
                          </div>
                          <SponsorChipRow
                            sponsors={chips}
                            color={set.color}
                            labelPrefix="Cashed"
                          />
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {TEAM_GROUPS.map((group) => (
                  <tr key={group.id}>
                    <th scope="row" className="freshmeet-hq-row-header">
                      <div className="freshmeet-hq-row-label">{group.label}</div>
                      <div className="freshmeet-hq-row-range">Team {group.range}</div>
                    </th>
                    {mode === 'stuffing' && (
                      (() => {
                        const setId = group.id === 'A' ? 'A' : group.id === 'B' ? 'B' : 'C';
                        const set = SET_META[setId];
                        const id =
                          setId === 'A'
                            ? 'DIVE1_A'
                            : setId === 'B'
                            ? 'DIVE1_B'
                            : 'DIVE1_C';
                        const isPlaced = !!placedMap[id];
                        return (
                          <td className="freshmeet-hq-cell-wrapper">
                            <button
                              type="button"
                              className="freshmeet-hq-cell freshmeet-hq-cell--button freshmeet-hq-cell--dive1"
                              style={{ backgroundColor: DIVE_COLORS['Dive 1'] || BG_NAVY }}
                              onClick={() => handleToggleExtra(id)}
                              aria-pressed={isPlaced}
                              aria-label={`${group.label}, Dive 1 + ${set.label}`}
                            >
                              <div className="freshmeet-hq-cell-text">
                                <span className="freshmeet-hq-cell-dive">Dive 1</span>
                                <span className="freshmeet-hq-cell-next-set">+ {set.label}</span>
                              </div>
                              <div className="freshmeet-hq-cell-next-sponsors">
                                <div className="freshmeet-hq-next-label">Starting sponsors</div>
                                <SponsorChipRow
                                  sponsors={sponsorsBySet[set.id]}
                                  color={set.color}
                                  labelPrefix="Dive 1 set"
                                />
                              </div>
                              <div
                                className={
                                  isPlaced
                                    ? 'freshmeet-hq-cell-overlay freshmeet-hq-cell-overlay--visible'
                                    : 'freshmeet-hq-cell-overlay'
                                }
                              >
                                <span className="freshmeet-hq-cell-overlay-check">✓ PLACED</span>
                              </div>
                            </button>
                          </td>
                        );
                      })()
                    )}
                    {CASHED_COLUMNS.map((col) => {
                      const cell = getCell(group.id, col.setId);
                      const isDive4Only = cell.diveLabel === 'Dive 4';
                      const isPlaced = !!placedMap[cell.id];
                      const isInteractive = cell.hasEnvelope && mode === 'stuffing';

                      const bgStyle = isDive4Only
                        ? {
                            backgroundColor: DIVE_COLORS[cell.diveLabel] || GOLD_FINAL,
                            borderColor: GOLD_FINAL,
                            borderStyle: 'dashed',
                          }
                        : {
                            backgroundColor: DIVE_COLORS[cell.diveLabel] || BG_NAVY,
                          };

                      const ariaLabelParts = [
                        group.label,
                        col.label,
                        isDive4Only ? 'Give Dive 4 only' : `Give ${cell.label}`,
                      ];

                      const commonContent = (
                        <>
                          <div className="freshmeet-hq-cell-text">
                            <span className="freshmeet-hq-cell-dive">{cell.diveLabel}</span>
                            {!isDive4Only && cell.nextSetId && (
                              <span className="freshmeet-hq-cell-next-set">
                                + {SET_META[cell.nextSetId].label}
                              </span>
                            )}
                            {isDive4Only && (
                              <span className="freshmeet-hq-cell-final-note">
                                Final round — give Dive 4 only
                              </span>
                            )}
                          </div>
                          {!isDive4Only && cell.nextSetId && (
                            <div className="freshmeet-hq-cell-next-sponsors">
                              <div className="freshmeet-hq-next-label">Next sponsors</div>
                              <SponsorChipRow
                                sponsors={sponsorsBySet[cell.nextSetId]}
                                color={SET_META[cell.nextSetId].color}
                                labelPrefix="Next"
                              />
                            </div>
                          )}
                          {cell.hasEnvelope && (
                            <div
                              className={
                                isPlaced
                                  ? 'freshmeet-hq-cell-overlay freshmeet-hq-cell-overlay--visible'
                                  : 'freshmeet-hq-cell-overlay'
                              }
                            >
                              <span className="freshmeet-hq-cell-overlay-check">✓ PLACED</span>
                            </div>
                          )}
                        </>
                      );

                      if (isInteractive) {
                        return (
                          <td key={cell.id} className="freshmeet-hq-cell-wrapper">
                            <button
                              type="button"
                              className="freshmeet-hq-cell freshmeet-hq-cell--button"
                              style={bgStyle}
                              onClick={() => handleToggleCell(cell)}
                              aria-pressed={isPlaced}
                              aria-label={ariaLabelParts.join('. ')}
                            >
                              {commonContent}
                            </button>
                          </td>
                        );
                      }

                      // Read-only cell (Dive 4 or in lookup mode)
                      return (
                        <td key={cell.id} className="freshmeet-hq-cell-wrapper">
                          <div
                            className={
                              isDive4Only
                                ? 'freshmeet-hq-cell freshmeet-hq-cell--final'
                                : 'freshmeet-hq-cell freshmeet-hq-cell--readonly'
                            }
                            style={bgStyle}
                            aria-label={ariaLabelParts.join('. ')}
                          >
                            {commonContent}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="freshmeet-hq-footnote">
            Cell color follows the dive being handed out. Gold dashed cells are Dive 4 only.
          </p>
        </section>
          </>
        )}
      </div>
    </div>
  );
};

export default FreshMeetHqDashboard;

